import { Button, Icon, IconName, Text } from '@flatfile/baseline'
import React from 'react'
import { OAuth } from '../../auth/oauth'
import { logger } from '../../utils/logging'

interface CustomErrorComponentProps {
  icon?: string // Optional custom icon
  title?: string // Optional custom title
  message?: string // Optional custom error message
  retryButtonText?: string // Optional custom text for the retry button
  statusButtonText?: string // Optional custom text for the status button
  error?: ErrorResponse | ErrorType // Optional error object
  page: string // The page where the error occurred
  onRetryClick?: () => void // Optional custom onClick handler for the retry button
  onStatusClick?: () => void // Optional custom onClick handler for the status button
  doNotCenter?: boolean //removes vertical align in center
}

type ErrorObject = {
  statusCode: number
  body: BodyObject
}

type BodyObject = {
  errors: ErrorDetail[]
}

type ErrorDetail = {
  key: string
  message: string
  code: string
}

export type ErrorResponse = {
  error: ErrorObject
}

type ConfigParams = {
  pageSize: number
  pageNumber: number
  app: string
}

type Config = {
  params: ConfigParams
}

type ErrorDataDetail = {
  key: string
  message: string
  code: string
}

type ErrorData = {
  errors: ErrorDataDetail[]
}

export type ErrorType = {
  config: Config
  type: string
  url: string
  redirected: boolean
  status: number
  ok: boolean
  statusText: string
  headers: Record<string, unknown> // Assuming headers can be key-value pairs of unknown types
  body: Record<string, unknown> // Assuming body can be key-value pairs of unknown types
  bodyUsed: boolean
  data: ErrorData
}

export const CustomErrorComponent: React.FC<CustomErrorComponentProps> = ({
  icon = 'triangle-exclamation',
  title = 'Oops! Something went wrong.', // Provide a default title
  message = 'An unexpected error has occurred.', // Provide a default message
  retryButtonText = 'Try Again', // Provide default text for the retry button
  statusButtonText = 'Check System Status', // Provide default text for the status button
  error, // Optional error object
  page, // The page where the error occurred
  onRetryClick, // Custom handler for retry button click
  onStatusClick, // Custom handler for status button click
  doNotCenter,
}) => {
  if (error) {
    logger.error(`Error caught in error boundary on ${page ?? 'unknown'} page`, error)
  }

  // Default action if no custom handler is provided for the retry button
  const defaultRetryAction = () => {
    window.location.reload()
  }

  // Default action if no custom handler is provided for the status button
  const defaultStatusAction = () => {
    window.open('https://status.flatfile.io', '_blank', 'noopener,noreferrer')
  }

  const BaseError = (
    <div className='flex flex-col items-center'>
      <div className='flex justify-center mt-8 mb-4'>
        <div className='bg-border-80 rounded-xs p-2' data-testid='empty-state-icon'>
          <Icon color='muted-foreground-100' size={18} variant='outline' name={icon as IconName} />
        </div>
      </div>
      <Text className='text-xl font-medium'>{title}</Text>
      {message.split('\n').map((sentence, index) => (
        <Text key={index} className={`text-text-default ${index === 0 ? 'mt-4' : 'mt-2'}`}>
          {sentence}
        </Text>
      ))}
      <div className='flex gap-2 justify-center mt-8'>
        <Button variant='primary' size='sm' onClick={onRetryClick || defaultRetryAction}>
          {retryButtonText}
        </Button>
        <Button variant='outline' size='sm' onClick={onStatusClick || defaultStatusAction}>
          {statusButtonText}
        </Button>
      </div>
    </div>
  )

  return doNotCenter ? (
    BaseError
  ) : (
    <div className='flex items-center justify-center h-screen bg-base-white'>{BaseError}</div>
  )
}

interface ErrorBoundaryErrorPageProps extends CustomErrorComponentProps {
  error?: ErrorResponse | ErrorType
  page: string
  logout?: boolean
}

export const ErrorBoundaryErrorPage = (props: ErrorBoundaryErrorPageProps) => {
  let code
  if (props.error && 'statusCode' in props.error) {
    code = props.error.statusCode
  } else if (props.error && 'status' in props.error) {
    code = props.error.status
  }
  return code === 403 || code === 401 ? NoAccessError(props) : CustomErrorComponent(props)
}

export const NoAccessError = (props: ErrorBoundaryErrorPageProps) => {
  let logoutProps = {}
  if (props.logout) {
    const handleLogout = () => OAuth.logout()
    logoutProps = {
      statusButtonText: 'Logout',
      onStatusClick: handleLogout,
    }
  }

  return (
    <CustomErrorComponent
      {...props}
      {...logoutProps}
      icon='lock'
      title='No Access'
      message="It looks like you don't have the permissions to view this page.
  If you believe this is an error, please contact your administrator to proceed."
    />
  )
}
